import clsx from 'clsx';
import type { VehicleLayout } from '@chukua/vehicle-model';
import { computePlanScene, type PlanMaterial, type PlanTyreShape } from '../policies/plan-scene';
import { selectableMarkProps } from './selectable-mark-props';

interface PlanDiagramProps {
  layout: VehicleLayout;
  selectedCode: string | null;
  onSelect?: (code: string) => void;
}

/** Opaque mixes with the surface, so a frame rail under the deck doesn't show through it. */
const OUTLINE_CLASSES: Record<PlanMaterial, string> = {
  steel: 'fill-[color-mix(in_srgb,var(--color-muted)_60%,var(--color-surface))] stroke-muted/70',
  paint: 'fill-surface stroke-line',
};

function PlanTyre({ tyre, isSelected, onSelect }: { tyre: PlanTyreShape; isSelected: boolean; onSelect?: (code: string) => void }) {
  const { x, y, width, height } = tyre.rect;
  return (
    <g
      {...selectableMarkProps({ code: tyre.code, name: tyre.name, isSelected, onSelect })}
      className={clsx(onSelect && 'cursor-pointer outline-none focus-visible:[&>rect]:stroke-primary')}
    >
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={6}
        strokeWidth={1.5}
        className={isSelected ? 'fill-primary stroke-primary' : 'fill-panel stroke-muted'}
      />
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        className={clsx('pointer-events-none font-mono text-[15px] font-medium', isSelected ? 'fill-white' : 'fill-panel-fg')}
      >
        {tyre.code || '·'}
      </text>
    </g>
  );
}

/** The chart tyre fitters already know: the vehicle from straight above, on its side with the front on the right. */
export function PlanDiagram({ layout, selectedCode, onSelect }: PlanDiagramProps) {
  const { viewBox, shapes } = computePlanScene(layout);
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        className="mx-auto block h-auto w-full min-w-[480px]"
        role="group"
        aria-label="Tyre position diagram, plan"
      >
        <text
          x={viewBox.x + viewBox.width - 6}
          y={viewBox.y + 16}
          textAnchor="end"
          className="fill-muted font-sans text-[13px] font-semibold tracking-[0.12em]"
          aria-hidden
        >
          FRONT →
        </text>
        <text x={viewBox.x + 6} y={viewBox.y + 16} className="fill-muted font-sans text-[13px] tracking-[0.06em]" aria-hidden>
          LEFT SIDE
        </text>
        {shapes.map((shape, index) =>
          shape.kind === 'rect' ? (
            <rect
              key={index}
              x={shape.x}
              y={shape.y}
              width={shape.width}
              height={shape.height}
              rx={4}
              strokeWidth={1.2}
              className={OUTLINE_CLASSES[shape.material]}
              data-material={shape.material}
              aria-hidden
            />
          ) : (
            <PlanTyre key={index} tyre={shape} isSelected={shape.code !== '' && shape.code === selectedCode} onSelect={onSelect} />
          ),
        )}
      </svg>
    </div>
  );
}
